import { useState } from "react";
import { useParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Typography,
  Skeleton,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  Tabs,
  Tab,
  CircularProgress,
  Badge,
} from "@mui/material";
import {
  Check as ApproveIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Refresh as RefreshIcon,
  Flag as FlagIcon,
  PlayArrow as AnalyzeIcon,
  BugReport as JiraIcon,
  OpenInNew as OpenIcon,
  AutoAwesome as AiIcon,
} from "@mui/icons-material";
import {
  teamApi,
  aiTodosApi,
  cacheReviewApi,
  AiTodo,
  CacheReviewEntry,
} from "@/services/api";
import api from "@/services/api";
import { History as HistoryIcon } from "@mui/icons-material";
import { useAuth } from "@/context/AuthContext";

interface CacheEntry {
  id: string;
  team_id: string;
  query_hash: string;
  original_query: string;
  response: string;
  context_type: string;
  confidence_score: number;
  hit_count: number;
  last_hit_at: string | null;
  is_approved: boolean;
  approved_by: string | null;
  is_active: boolean;
  is_flagged_bad: boolean;
  flagged_by: string | null;
  flagged_at: string | null;
  expires_at: string | null;
  created_at: string;
  updated_at: string;
}

interface CacheStats {
  total_entries: number;
  active_entries: number;
  total_hits: number;
  pending_approval: number;
  flagged_bad: number;
}

type TodoStatus = "pending" | "in_progress" | "completed" | "dismissed";
type TodoPriority = "low" | "medium" | "high";

const priorityColors: Record<
  TodoPriority,
  { bg: string; color: string; label: string }
> = {
  low: { bg: "#DCFCE7", color: "#166534", label: "Low" },
  medium: { bg: "#FEF3C7", color: "#92400E", label: "Medium" },
  high: { bg: "#FEE2E2", color: "#991B1B", label: "High" },
};

const statusColors: Record<
  TodoStatus,
  { bg: string; color: string; label: string }
> = {
  pending: { bg: "#FEF3C7", color: "#92400E", label: "Pending" },
  in_progress: { bg: "#DBEAFE", color: "#1E40AF", label: "In Progress" },
  completed: { bg: "#DCFCE7", color: "#166534", label: "Completed" },
  dismissed: { bg: "#F3F4F6", color: "#6B7280", label: "Dismissed" },
};

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

interface CacheReviewPageProps {
  embeddedTeamId?: string;
}

export default function CacheReviewPage({ embeddedTeamId }: CacheReviewPageProps) {
  const { teamId: paramTeamId } = useParams<{ teamId?: string }>();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const isEmbedded = !!embeddedTeamId;
  const [selectedTeamId, setSelectedTeamId] = useState<string>(
    paramTeamId || "",
  );
  const [selectedEntry, setSelectedEntry] = useState<CacheEntry | null>(null);
  const [editDialog, setEditDialog] = useState(false);
  const [viewDialog, setViewDialog] = useState(false);
  const [editedResponse, setEditedResponse] = useState("");
  const [tabValue, setTabValue] = useState(0);

  // Cache Review pagination
  const [cachePage, setCachePage] = useState(0);
  const [cacheRowsPerPage, setCacheRowsPerPage] = useState(25);

  // AI Todos state
  const [selectedTodo, setSelectedTodo] = useState<AiTodo | null>(null);
  const [todoViewDialog, setTodoViewDialog] = useState(false);
  const [analyzeDialog, setAnalyzeDialog] = useState(false);
  const [analyzeYear, setAnalyzeYear] = useState(new Date().getFullYear());
  const [analyzeMonth, setAnalyzeMonth] = useState(new Date().getMonth() || 12); // Previous month or December
  const [todoStatusFilter, setTodoStatusFilter] = useState<string>("");
  const [todoPriorityFilter, setTodoPriorityFilter] = useState<string>("");
  const [todoPage, setTodoPage] = useState(0);
  const [todoRowsPerPage, setTodoRowsPerPage] = useState(25);

  // All Interactions state
  const [interactionsPage, setInteractionsPage] = useState(0);
  const [interactionsRowsPerPage, setInteractionsRowsPerPage] = useState(25);
  const [interactionsContextFilter, setInteractionsContextFilter] =
    useState<string>("");
  const [selectedInteraction, setSelectedInteraction] =
    useState<CacheReviewEntry | null>(null);
  const [interactionViewDialog, setInteractionViewDialog] = useState(false);

  const teamId = embeddedTeamId || selectedTeamId || paramTeamId;

  const { data: teams } = useQuery({
    queryKey: ["teams"],
    queryFn: () => teamApi.list({ is_active: true, limit: 100 }).then((res) => res.data.items),
    enabled: !isEmbedded,
  });

  const approvedTeams =
    teams?.filter((t) => t.approval_status === "approved") || [];

  // Cache Review queries
  const { data: cacheData, isLoading: cacheLoading } = useQuery({
    queryKey: ["cache-entries", teamId, cachePage, cacheRowsPerPage],
    queryFn: () =>
      api
        .get<{ total: number; entries: CacheEntry[] }>(
          `/cache/responses/${teamId}`,
          {
            params: {
              limit: cacheRowsPerPage,
              offset: cachePage * cacheRowsPerPage,
              include_inactive: true,
            },
          },
        )
        .then((res) => res.data),
    enabled: !!teamId,
  });

  const { data: cacheStats } = useQuery({
    queryKey: ["cache-stats", teamId],
    queryFn: () =>
      api
        .get<CacheStats>(`/cache/responses/${teamId}/stats`)
        .then((res) => res.data),
    enabled: !!teamId,
  });

  // AI Todos queries
  const { data: todosData, isLoading: todosLoading } = useQuery({
    queryKey: [
      "ai-todos",
      teamId,
      todoStatusFilter,
      todoPriorityFilter,
      todoPage,
      todoRowsPerPage,
    ],
    queryFn: () =>
      aiTodosApi
        .list({
          team_id: teamId || undefined,
          status: todoStatusFilter || undefined,
          priority: todoPriorityFilter || undefined,
          page: todoPage + 1,
          page_size: todoRowsPerPage,
        })
        .then((res) => res.data),
    enabled: !!teamId,
  });

  const { data: todoStats } = useQuery({
    queryKey: ["ai-todos-stats", teamId],
    queryFn: () =>
      aiTodosApi.getStats(teamId || undefined).then((res) => res.data),
    enabled: !!teamId,
  });

  const { data: unanalyzedData } = useQuery({
    queryKey: ["unanalyzed-count", teamId],
    queryFn: () =>
      aiTodosApi.getUnanalyzedCount(teamId!).then((res) => res.data),
    enabled: !!teamId,
  });

  // All Interactions queries
  const { data: interactionsData, isLoading: interactionsLoading } = useQuery({
    queryKey: [
      "all-interactions",
      teamId,
      interactionsContextFilter,
      interactionsPage,
      interactionsRowsPerPage,
    ],
    queryFn: () =>
      cacheReviewApi
        .list(teamId!, {
          context_type: interactionsContextFilter || undefined,
          page: interactionsPage + 1,
          page_size: interactionsRowsPerPage,
        })
        .then((res) => res.data),
    enabled: !!teamId,
  });

  const { data: interactionsStats } = useQuery({
    queryKey: ["all-interactions-stats", teamId],
    queryFn: () => cacheReviewApi.getStats(teamId!).then((res) => res.data),
    enabled: !!teamId,
  });

  // Cache mutations
  const updateMutation = useMutation({
    mutationFn: ({
      cacheId,
      data,
    }: {
      cacheId: string;
      data: { response?: string; is_approved?: boolean; is_active?: boolean };
    }) =>
      api.patch(`/cache/responses/${teamId}/${cacheId}`, data, {
        params: { approved_by: user?.email },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["cache-entries", teamId] });
      queryClient.invalidateQueries({ queryKey: ["cache-stats", teamId] });
      setEditDialog(false);
      setSelectedEntry(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (cacheId: string) =>
      api.delete(`/cache/responses/${teamId}/${cacheId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["cache-entries", teamId] });
      queryClient.invalidateQueries({ queryKey: ["cache-stats", teamId] });
    },
  });

  const cleanupMutation = useMutation({
    mutationFn: () => api.post(`/cache/responses/${teamId}/cleanup`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["cache-entries", teamId] });
      queryClient.invalidateQueries({ queryKey: ["cache-stats", teamId] });
    },
  });

  // AI Todos mutations
  const analyzeMutation = useMutation({
    mutationFn: () =>
      aiTodosApi.analyze({
        team_id: teamId!,
        year: analyzeYear,
        month: analyzeMonth,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ai-todos"] });
      queryClient.invalidateQueries({ queryKey: ["ai-todos-stats"] });
      queryClient.invalidateQueries({ queryKey: ["unanalyzed-count"] });
      setAnalyzeDialog(false);
    },
  });

  const completeTodoMutation = useMutation({
    mutationFn: (todoId: string) => aiTodosApi.complete(todoId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ai-todos"] });
      queryClient.invalidateQueries({ queryKey: ["ai-todos-stats"] });
    },
  });

  const dismissTodoMutation = useMutation({
    mutationFn: (todoId: string) => aiTodosApi.dismiss(todoId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ai-todos"] });
      queryClient.invalidateQueries({ queryKey: ["ai-todos-stats"] });
    },
  });

  const createJiraMutation = useMutation({
    mutationFn: (todoId: string) => aiTodosApi.createJira(todoId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ai-todos"] });
    },
  });

  // Cache handlers
  const handleApprove = (entry: CacheEntry) => {
    updateMutation.mutate({ cacheId: entry.id, data: { is_approved: true } });
  };

  const handleEdit = (entry: CacheEntry) => {
    setSelectedEntry(entry);
    setEditedResponse(entry.response);
    setEditDialog(true);
  };

  const handleSaveEdit = () => {
    if (selectedEntry) {
      updateMutation.mutate({
        cacheId: selectedEntry.id,
        data: { response: editedResponse, is_approved: true },
      });
    }
  };

  const handleDelete = (entry: CacheEntry) => {
    if (confirm("Are you sure you want to delete this cached response?")) {
      deleteMutation.mutate(entry.id);
    }
  };

  const handleView = (entry: CacheEntry) => {
    setSelectedEntry(entry);
    setViewDialog(true);
  };

  // AI Todos handlers
  const handleViewTodo = (todo: AiTodo) => {
    setSelectedTodo(todo);
    setTodoViewDialog(true);
  };

  const handleCompleteTodo = (todo: AiTodo) => {
    completeTodoMutation.mutate(todo.id);
  };

  const handleDismissTodo = (todo: AiTodo) => {
    if (confirm("Are you sure you want to dismiss this todo?")) {
      dismissTodoMutation.mutate(todo.id);
    }
  };

  const handleCreateJira = (todo: AiTodo) => {
    createJiraMutation.mutate(todo.id);
  };

  // All Interactions handlers
  const handleViewInteraction = (entry: CacheReviewEntry) => {
    setSelectedInteraction(entry);
    setInteractionViewDialog(true);
  };

  const getRowBackgroundColor = (entry: CacheEntry) => {
    if (entry.is_flagged_bad) return "#FFF7ED";
    if (!entry.is_active) return "#F3F4F6";
    return undefined;
  };

  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const pendingTodosCount = todoStats?.by_status?.pending || 0;

  return (
    <Box>
      {!isEmbedded && (
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            justifyContent: "space-between",
            alignItems: { xs: "stretch", sm: "center" },
            gap: 2,
            mb: 3,
          }}
        >
          <Typography variant="h5">Bot Interactions</Typography>
          <FormControl size="small" sx={{ minWidth: { xs: "100%", sm: 200 } }}>
            <InputLabel>Team</InputLabel>
            <Select
              value={selectedTeamId}
              label="Team"
              onChange={(e) => {
                setSelectedTeamId(e.target.value);
                setCachePage(0);
                setTodoPage(0);
              }}
            >
              {approvedTeams.map((team) => (
                <MenuItem key={team.id} value={team.id}>
                  {team.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      )}

      {!teamId ? (
        <Alert severity="info">
          Select a team to view bot interactions and AI todos
        </Alert>
      ) : (
        <>
          <Tabs
            value={tabValue}
            onChange={(_, newValue) => setTabValue(newValue)}
            sx={{ borderBottom: 1, borderColor: "divider" }}
          >
            <Tab
              label={
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <HistoryIcon fontSize="small" />
                  All Interactions
                </Box>
              }
            />
            <Tab label="Semantic Cache" />
            <Tab
              label={
                <Badge
                  badgeContent={pendingTodosCount}
                  color="warning"
                  max={99}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <AiIcon fontSize="small" />
                    AI Todos
                  </Box>
                </Badge>
              }
            />
          </Tabs>

          {/* All Interactions Tab */}
          <TabPanel value={tabValue} index={0}>
            {interactionsLoading ? (
              <Skeleton variant="rectangular" height={400} />
            ) : (
              <>
                {/* Stats Cards */}
                <Grid container spacing={3} sx={{ mb: 4 }}>
                  <Grid item xs={6} sm={3}>
                    <Card>
                      <CardContent sx={{ py: 2 }}>
                        <Typography color="text.secondary" variant="body2">
                          Total Interactions
                        </Typography>
                        <Typography variant="h5">
                          {interactionsStats?.total || 0}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Card>
                      <CardContent sx={{ py: 2 }}>
                        <Typography color="text.secondary" variant="body2">
                          Freeform
                        </Typography>
                        <Typography variant="h5">
                          {interactionsStats?.by_context_type?.freeform || 0}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Card>
                      <CardContent sx={{ py: 2 }}>
                        <Typography color="text.secondary" variant="body2">
                          JIRA
                        </Typography>
                        <Typography variant="h5">
                          {interactionsStats?.by_context_type?.jira || 0}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Card>
                      <CardContent sx={{ py: 2 }}>
                        <Typography color="text.secondary" variant="body2">
                          GitHub
                        </Typography>
                        <Typography variant="h5">
                          {interactionsStats?.by_context_type?.github || 0}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>

                {/* Filters */}
                <Box
                  sx={{
                    display: "flex",
                    gap: 2,
                    mb: 3,
                    flexWrap: "wrap",
                    alignItems: "center",
                  }}
                >
                  <Button
                    variant="outlined"
                    startIcon={<RefreshIcon />}
                    onClick={() => {
                      queryClient.invalidateQueries({
                        queryKey: ["all-interactions", teamId],
                      });
                      queryClient.invalidateQueries({
                        queryKey: ["all-interactions-stats", teamId],
                      });
                    }}
                  >
                    Refresh
                  </Button>
                  <FormControl size="small" sx={{ minWidth: 150 }}>
                    <InputLabel>Context Type</InputLabel>
                    <Select
                      value={interactionsContextFilter}
                      label="Context Type"
                      onChange={(e) => {
                        setInteractionsContextFilter(e.target.value);
                        setInteractionsPage(0);
                      }}
                    >
                      <MenuItem value="">All</MenuItem>
                      <MenuItem value="freeform">Freeform</MenuItem>
                      <MenuItem value="jira">JIRA</MenuItem>
                      <MenuItem value="github">GitHub</MenuItem>
                      <MenuItem value="web_lookup">Web Lookup</MenuItem>
                    </Select>
                  </FormControl>
                </Box>

                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Query</TableCell>
                        <TableCell>Type</TableCell>
                        <TableCell>User</TableCell>
                        <TableCell>Time (ms)</TableCell>
                        <TableCell>Date</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {interactionsData?.items.map((entry) => (
                        <TableRow
                          key={entry.id}
                          sx={{
                            bgcolor: entry.was_cache_hit
                              ? "#DCFCE7"
                              : undefined,
                          }}
                        >
                          <TableCell>
                            <Typography
                              variant="body2"
                              sx={{
                                maxWidth: 350,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {entry.user_query}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={entry.context_type}
                              size="small"
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>
                            <Typography
                              variant="body2"
                              sx={{
                                maxWidth: 150,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {entry.user_email || "-"}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                              }}
                            >
                              {entry.processing_time_ms || "-"}
                              {entry.was_cache_hit && (
                                <Chip
                                  label="Hit"
                                  size="small"
                                  color="success"
                                  sx={{ height: 20, fontSize: "0.7rem" }}
                                />
                              )}
                            </Box>
                          </TableCell>
                          <TableCell>
                            {new Date(entry.created_at).toLocaleString()}
                          </TableCell>
                          <TableCell align="right">
                            <Tooltip title="View Details">
                              <IconButton
                                size="small"
                                onClick={() => handleViewInteraction(entry)}
                              >
                                <ViewIcon />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                      {(!interactionsData?.items ||
                        interactionsData.items.length === 0) && (
                        <TableRow>
                          <TableCell colSpan={6} align="center">
                            <Typography color="text.secondary" sx={{ py: 4 }}>
                              No interactions recorded yet. Bot conversations
                              will appear here.
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                  <TablePagination
                    rowsPerPageOptions={[10, 25, 50, 100]}
                    component="div"
                    count={interactionsData?.total || 0}
                    rowsPerPage={interactionsRowsPerPage}
                    page={interactionsPage}
                    onPageChange={(_, newPage) => setInteractionsPage(newPage)}
                    onRowsPerPageChange={(e) => {
                      setInteractionsRowsPerPage(parseInt(e.target.value, 10));
                      setInteractionsPage(0);
                    }}
                  />
                </TableContainer>
              </>
            )}
          </TabPanel>

          {/* Semantic Cache Tab */}
          <TabPanel value={tabValue} index={1}>
            {cacheLoading ? (
              <Skeleton variant="rectangular" height={400} />
            ) : (
              <>
                {/* Stats Cards */}
                <Grid container spacing={3} sx={{ mb: 4 }}>
                  <Grid item xs={6} sm={4} md={2.4}>
                    <Card>
                      <CardContent sx={{ py: 2 }}>
                        <Typography color="text.secondary" variant="body2">
                          Total Entries
                        </Typography>
                        <Typography variant="h5">
                          {cacheStats?.total_entries || 0}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={6} sm={4} md={2.4}>
                    <Card>
                      <CardContent sx={{ py: 2 }}>
                        <Typography color="text.secondary" variant="body2">
                          Active Entries
                        </Typography>
                        <Typography variant="h5">
                          {cacheStats?.active_entries || 0}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={6} sm={4} md={2.4}>
                    <Card>
                      <CardContent sx={{ py: 2 }}>
                        <Typography color="text.secondary" variant="body2">
                          Total Cache Hits
                        </Typography>
                        <Typography variant="h5">
                          {cacheStats?.total_hits || 0}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={6} sm={4} md={2.4}>
                    <Card
                      sx={{
                        bgcolor: cacheStats?.pending_approval
                          ? "#FEF3C7"
                          : undefined,
                      }}
                    >
                      <CardContent sx={{ py: 2 }}>
                        <Typography
                          color={
                            cacheStats?.pending_approval
                              ? "#92400E"
                              : "text.secondary"
                          }
                          variant="body2"
                        >
                          Pending Review
                        </Typography>
                        <Typography
                          variant="h5"
                          color={
                            cacheStats?.pending_approval ? "#92400E" : undefined
                          }
                        >
                          {cacheStats?.pending_approval || 0}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={6} sm={4} md={2.4}>
                    <Card
                      sx={{
                        bgcolor: cacheStats?.flagged_bad
                          ? "#FFF7ED"
                          : undefined,
                      }}
                    >
                      <CardContent sx={{ py: 2 }}>
                        <Typography
                          color={
                            cacheStats?.flagged_bad
                              ? "#C2410C"
                              : "text.secondary"
                          }
                          variant="body2"
                        >
                          Flagged Bad
                        </Typography>
                        <Typography
                          variant="h5"
                          color={
                            cacheStats?.flagged_bad ? "#C2410C" : undefined
                          }
                        >
                          {cacheStats?.flagged_bad || 0}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>

                <Box sx={{ mb: 2, display: "flex", gap: 2 }}>
                  <Button
                    variant="outlined"
                    startIcon={<RefreshIcon />}
                    onClick={() => {
                      queryClient.invalidateQueries({
                        queryKey: ["cache-entries", teamId],
                      });
                      queryClient.invalidateQueries({
                        queryKey: ["cache-stats", teamId],
                      });
                    }}
                  >
                    Refresh
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<RefreshIcon />}
                    onClick={() => cleanupMutation.mutate()}
                    disabled={cleanupMutation.isPending}
                  >
                    Cleanup Expired
                  </Button>
                </Box>

                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Query</TableCell>
                        <TableCell>Hits</TableCell>
                        <TableCell>Type</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Last Hit</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {cacheData?.entries.map((entry) => (
                        <TableRow
                          key={entry.id}
                          sx={{ bgcolor: getRowBackgroundColor(entry) }}
                        >
                          <TableCell>
                            <Typography
                              variant="body2"
                              sx={{
                                maxWidth: 300,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {entry.original_query}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={entry.hit_count}
                              size="small"
                              color="primary"
                            />
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={entry.context_type}
                              size="small"
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>
                            <Box
                              sx={{
                                display: "flex",
                                gap: 0.5,
                                flexWrap: "wrap",
                              }}
                            >
                              {entry.is_flagged_bad && (
                                <Tooltip
                                  title={`Flagged by ${entry.flagged_by || "unknown"}`}
                                >
                                  <Chip
                                    icon={<FlagIcon />}
                                    label="Bad"
                                    size="small"
                                    sx={{
                                      bgcolor: "#FFEDD5",
                                      color: "#C2410C",
                                      "& .MuiChip-icon": { color: "#C2410C" },
                                    }}
                                  />
                                </Tooltip>
                              )}
                              {entry.is_approved ? (
                                <Chip
                                  label="Approved"
                                  size="small"
                                  color="success"
                                />
                              ) : (
                                <Chip
                                  label="Pending"
                                  size="small"
                                  color="warning"
                                />
                              )}
                              {!entry.is_active && (
                                <Chip
                                  label="Inactive"
                                  size="small"
                                  color="default"
                                />
                              )}
                            </Box>
                          </TableCell>
                          <TableCell>
                            {entry.last_hit_at
                              ? new Date(entry.last_hit_at).toLocaleDateString()
                              : "Never"}
                          </TableCell>
                          <TableCell align="right">
                            <Tooltip title="View">
                              <IconButton
                                size="small"
                                onClick={() => handleView(entry)}
                              >
                                <ViewIcon />
                              </IconButton>
                            </Tooltip>
                            {!entry.is_approved && (
                              <Tooltip title="Approve">
                                <IconButton
                                  size="small"
                                  color="success"
                                  onClick={() => handleApprove(entry)}
                                >
                                  <ApproveIcon />
                                </IconButton>
                              </Tooltip>
                            )}
                            <Tooltip title="Edit">
                              <IconButton
                                size="small"
                                onClick={() => handleEdit(entry)}
                              >
                                <EditIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleDelete(entry)}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                      {(!cacheData?.entries ||
                        cacheData.entries.length === 0) && (
                        <TableRow>
                          <TableCell colSpan={6} align="center">
                            <Typography color="text.secondary" sx={{ py: 4 }}>
                              No cached responses yet
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                  <TablePagination
                    rowsPerPageOptions={[10, 25, 50, 100]}
                    component="div"
                    count={cacheData?.total || 0}
                    rowsPerPage={cacheRowsPerPage}
                    page={cachePage}
                    onPageChange={(_, newPage) => setCachePage(newPage)}
                    onRowsPerPageChange={(e) => {
                      setCacheRowsPerPage(parseInt(e.target.value, 10));
                      setCachePage(0);
                    }}
                  />
                </TableContainer>
              </>
            )}
          </TabPanel>

          {/* AI Todos Tab */}
          <TabPanel value={tabValue} index={2}>
            {todosLoading ? (
              <Skeleton variant="rectangular" height={400} />
            ) : (
              <>
                {/* Stats Cards */}
                <Grid container spacing={3} sx={{ mb: 4 }}>
                  <Grid item xs={6} sm={3}>
                    <Card>
                      <CardContent sx={{ py: 2 }}>
                        <Typography color="text.secondary" variant="body2">
                          Total
                        </Typography>
                        <Typography variant="h5">
                          {todoStats?.total || 0}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Card
                      sx={{
                        bgcolor: pendingTodosCount > 0 ? "#FEF3C7" : undefined,
                      }}
                    >
                      <CardContent sx={{ py: 2 }}>
                        <Typography
                          color={
                            pendingTodosCount > 0 ? "#92400E" : "text.secondary"
                          }
                          variant="body2"
                        >
                          Pending
                        </Typography>
                        <Typography
                          variant="h5"
                          color={pendingTodosCount > 0 ? "#92400E" : undefined}
                        >
                          {pendingTodosCount}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Card
                      sx={{
                        bgcolor:
                          (todoStats?.by_status?.in_progress || 0) > 0
                            ? "#DBEAFE"
                            : undefined,
                      }}
                    >
                      <CardContent sx={{ py: 2 }}>
                        <Typography
                          color={
                            (todoStats?.by_status?.in_progress || 0) > 0
                              ? "#1E40AF"
                              : "text.secondary"
                          }
                          variant="body2"
                        >
                          In Progress
                        </Typography>
                        <Typography
                          variant="h5"
                          color={
                            (todoStats?.by_status?.in_progress || 0) > 0
                              ? "#1E40AF"
                              : undefined
                          }
                        >
                          {todoStats?.by_status?.in_progress || 0}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Card
                      sx={{
                        bgcolor:
                          (todoStats?.by_status?.completed || 0) > 0
                            ? "#DCFCE7"
                            : undefined,
                      }}
                    >
                      <CardContent sx={{ py: 2 }}>
                        <Typography
                          color={
                            (todoStats?.by_status?.completed || 0) > 0
                              ? "#166534"
                              : "text.secondary"
                          }
                          variant="body2"
                        >
                          Completed
                        </Typography>
                        <Typography
                          variant="h5"
                          color={
                            (todoStats?.by_status?.completed || 0) > 0
                              ? "#166534"
                              : undefined
                          }
                        >
                          {todoStats?.by_status?.completed || 0}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>

                {/* Analyze Button & Filters */}
                <Box
                  sx={{
                    display: "flex",
                    gap: 2,
                    mb: 3,
                    flexWrap: "wrap",
                    alignItems: "center",
                  }}
                >
                  <Button
                    variant="outlined"
                    startIcon={<RefreshIcon />}
                    onClick={() => {
                      queryClient.invalidateQueries({ queryKey: ["ai-todos"] });
                      queryClient.invalidateQueries({
                        queryKey: ["ai-todos-stats"],
                      });
                      queryClient.invalidateQueries({
                        queryKey: ["unanalyzed-count"],
                      });
                    }}
                  >
                    Refresh
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<AnalyzeIcon />}
                    onClick={() => setAnalyzeDialog(true)}
                  >
                    Analyze Patterns
                    {unanalyzedData?.unanalyzed_count ? (
                      <Chip
                        label={unanalyzedData.unanalyzed_count}
                        size="small"
                        sx={{ ml: 1, bgcolor: "white", color: "primary.main" }}
                      />
                    ) : null}
                  </Button>
                  <FormControl size="small" sx={{ minWidth: 150 }}>
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={todoStatusFilter}
                      label="Status"
                      onChange={(e) => {
                        setTodoStatusFilter(e.target.value);
                        setTodoPage(0);
                      }}
                    >
                      <MenuItem value="">All</MenuItem>
                      <MenuItem value="pending">Pending</MenuItem>
                      <MenuItem value="in_progress">In Progress</MenuItem>
                      <MenuItem value="completed">Completed</MenuItem>
                      <MenuItem value="dismissed">Dismissed</MenuItem>
                    </Select>
                  </FormControl>
                  <FormControl size="small" sx={{ minWidth: 150 }}>
                    <InputLabel>Priority</InputLabel>
                    <Select
                      value={todoPriorityFilter}
                      label="Priority"
                      onChange={(e) => {
                        setTodoPriorityFilter(e.target.value);
                        setTodoPage(0);
                      }}
                    >
                      <MenuItem value="">All</MenuItem>
                      <MenuItem value="high">High</MenuItem>
                      <MenuItem value="medium">Medium</MenuItem>
                      <MenuItem value="low">Low</MenuItem>
                    </Select>
                  </FormControl>
                </Box>

                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Title</TableCell>
                        <TableCell align="center">Occurrences</TableCell>
                        <TableCell align="center">Priority</TableCell>
                        <TableCell align="center">Status</TableCell>
                        <TableCell>Source Month</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {todosData?.items.map((todo) => (
                        <TableRow key={todo.id}>
                          <TableCell>
                            <Typography
                              variant="body2"
                              sx={{
                                maxWidth: 350,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {todo.title}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Chip
                              label={todo.occurrence_count}
                              size="small"
                              color="primary"
                            />
                          </TableCell>
                          <TableCell align="center">
                            <Chip
                              label={
                                priorityColors[todo.priority as TodoPriority]
                                  ?.label || todo.priority
                              }
                              size="small"
                              sx={{
                                bgcolor:
                                  priorityColors[todo.priority as TodoPriority]
                                    ?.bg,
                                color:
                                  priorityColors[todo.priority as TodoPriority]
                                    ?.color,
                              }}
                            />
                          </TableCell>
                          <TableCell align="center">
                            <Chip
                              label={
                                statusColors[todo.status as TodoStatus]
                                  ?.label || todo.status
                              }
                              size="small"
                              sx={{
                                bgcolor:
                                  statusColors[todo.status as TodoStatus]?.bg,
                                color:
                                  statusColors[todo.status as TodoStatus]
                                    ?.color,
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            {todo.source_month
                              ? new Date(todo.source_month).toLocaleDateString(
                                  "en-US",
                                  { year: "numeric", month: "short" },
                                )
                              : "-"}
                          </TableCell>
                          <TableCell align="right">
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "flex-end",
                                gap: 0.5,
                              }}
                            >
                              <Tooltip title="View Details">
                                <IconButton
                                  size="small"
                                  onClick={() => handleViewTodo(todo)}
                                >
                                  <ViewIcon />
                                </IconButton>
                              </Tooltip>
                              {todo.jira_ticket_key ? (
                                <Tooltip
                                  title={`Open ${todo.jira_ticket_key} in JIRA`}
                                >
                                  <Chip
                                    icon={<JiraIcon />}
                                    label={todo.jira_ticket_key}
                                    size="small"
                                    color="primary"
                                    variant="outlined"
                                    onClick={() =>
                                      todo.jira_ticket_url &&
                                      window.open(
                                        todo.jira_ticket_url,
                                        "_blank",
                                      )
                                    }
                                    clickable={!!todo.jira_ticket_url}
                                    sx={{ cursor: "pointer" }}
                                  />
                                </Tooltip>
                              ) : (
                                todo.status !== "dismissed" && (
                                  <Tooltip title="Create JIRA Ticket">
                                    <IconButton
                                      size="small"
                                      color="primary"
                                      onClick={() => handleCreateJira(todo)}
                                      disabled={createJiraMutation.isPending}
                                    >
                                      {createJiraMutation.isPending ? (
                                        <CircularProgress size={18} />
                                      ) : (
                                        <JiraIcon />
                                      )}
                                    </IconButton>
                                  </Tooltip>
                                )
                              )}
                              {todo.status !== "completed" &&
                                todo.status !== "dismissed" && (
                                  <Tooltip title="Mark Complete">
                                    <IconButton
                                      size="small"
                                      color="success"
                                      onClick={() => handleCompleteTodo(todo)}
                                      disabled={completeTodoMutation.isPending}
                                    >
                                      <ApproveIcon />
                                    </IconButton>
                                  </Tooltip>
                                )}
                              {todo.status !== "dismissed" && (
                                <Tooltip title="Dismiss">
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => handleDismissTodo(todo)}
                                    disabled={dismissTodoMutation.isPending}
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                      {(!todosData?.items || todosData.items.length === 0) && (
                        <TableRow>
                          <TableCell colSpan={6} align="center">
                            <Typography color="text.secondary" sx={{ py: 4 }}>
                              No AI todos yet. Click "Analyze Patterns" to scan
                              for recurring issues.
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                  <TablePagination
                    rowsPerPageOptions={[10, 25, 50, 100]}
                    component="div"
                    count={todosData?.total || 0}
                    rowsPerPage={todoRowsPerPage}
                    page={todoPage}
                    onPageChange={(_, newPage) => setTodoPage(newPage)}
                    onRowsPerPageChange={(e) => {
                      setTodoRowsPerPage(parseInt(e.target.value, 10));
                      setTodoPage(0);
                    }}
                  />
                </TableContainer>
              </>
            )}
          </TabPanel>
        </>
      )}

      {/* Cache View Dialog */}
      <Dialog
        open={viewDialog}
        onClose={() => setViewDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Cached Response Details
          {selectedEntry?.is_flagged_bad && (
            <Chip
              icon={<FlagIcon />}
              label="Flagged as Bad"
              size="small"
              sx={{
                ml: 2,
                bgcolor: "#FFEDD5",
                color: "#C2410C",
                "& .MuiChip-icon": { color: "#C2410C" },
              }}
            />
          )}
        </DialogTitle>
        <DialogContent>
          {selectedEntry && (
            <Box
              sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}
            >
              {selectedEntry.is_flagged_bad && (
                <Alert severity="warning" sx={{ bgcolor: "#FFF7ED" }}>
                  This response was flagged as bad/incorrect by{" "}
                  {selectedEntry.flagged_by || "a user"}
                  {selectedEntry.flagged_at &&
                    ` on ${new Date(selectedEntry.flagged_at).toLocaleString()}`}
                </Alert>
              )}
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Original Query
                </Typography>
                <Typography variant="body1">
                  {selectedEntry.original_query}
                </Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Cached Response
                </Typography>
                <Paper
                  sx={{
                    p: 2,
                    bgcolor: selectedEntry.is_flagged_bad
                      ? "#FFF7ED"
                      : "grey.50",
                  }}
                >
                  <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                    {selectedEntry.response}
                  </Typography>
                </Paper>
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Hit Count
                  </Typography>
                  <Typography variant="body1">
                    {selectedEntry.hit_count}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Confidence Score
                  </Typography>
                  <Typography variant="body1">
                    {(selectedEntry.confidence_score * 100).toFixed(0)}%
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Created
                  </Typography>
                  <Typography variant="body1">
                    {new Date(selectedEntry.created_at).toLocaleString()}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Last Hit
                  </Typography>
                  <Typography variant="body1">
                    {selectedEntry.last_hit_at
                      ? new Date(selectedEntry.last_hit_at).toLocaleString()
                      : "Never"}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialog(false)}>Close</Button>
          <Button
            variant="contained"
            onClick={() => {
              setViewDialog(false);
              if (selectedEntry) handleEdit(selectedEntry);
            }}
          >
            Edit Response
          </Button>
        </DialogActions>
      </Dialog>

      {/* Cache Edit Dialog */}
      <Dialog
        open={editDialog}
        onClose={() => setEditDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Edit Cached Response</DialogTitle>
        <DialogContent>
          {selectedEntry && (
            <Box
              sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}
            >
              {selectedEntry.is_flagged_bad && (
                <Alert severity="info">
                  This response was flagged as bad. Editing and saving will
                  re-approve it and clear the flag.
                </Alert>
              )}
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Original Query
                </Typography>
                <Typography variant="body1">
                  {selectedEntry.original_query}
                </Typography>
              </Box>
              <TextField
                label="Response"
                multiline
                rows={10}
                fullWidth
                value={editedResponse}
                onChange={(e) => setEditedResponse(e.target.value)}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSaveEdit}
            disabled={updateMutation.isPending}
          >
            {updateMutation.isPending ? "Saving..." : "Save & Approve"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* AI Todo View Dialog */}
      <Dialog
        open={todoViewDialog}
        onClose={() => setTodoViewDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          AI Todo Details
          {selectedTodo?.jira_ticket_key && (
            <Chip
              icon={<JiraIcon />}
              label={selectedTodo.jira_ticket_key}
              size="small"
              sx={{ ml: 2 }}
              onClick={() =>
                selectedTodo.jira_ticket_url &&
                window.open(selectedTodo.jira_ticket_url, "_blank")
              }
              clickable={!!selectedTodo.jira_ticket_url}
              deleteIcon={<OpenIcon />}
              onDelete={() =>
                selectedTodo.jira_ticket_url &&
                window.open(selectedTodo.jira_ticket_url, "_blank")
              }
            />
          )}
        </DialogTitle>
        <DialogContent>
          {selectedTodo && (
            <Box
              sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}
            >
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Title
                </Typography>
                <Typography variant="body1">{selectedTodo.title}</Typography>
              </Box>
              {selectedTodo.description && (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Description
                  </Typography>
                  <Paper sx={{ p: 2, bgcolor: "grey.50" }}>
                    <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                      {selectedTodo.description}
                    </Typography>
                  </Paper>
                </Box>
              )}
              <Grid container spacing={2}>
                <Grid item xs={6} sm={3}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Occurrences
                  </Typography>
                  <Typography variant="body1">
                    {selectedTodo.occurrence_count}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Priority
                  </Typography>
                  <Chip
                    label={
                      priorityColors[selectedTodo.priority as TodoPriority]
                        ?.label || selectedTodo.priority
                    }
                    size="small"
                    sx={{
                      bgcolor:
                        priorityColors[selectedTodo.priority as TodoPriority]
                          ?.bg,
                      color:
                        priorityColors[selectedTodo.priority as TodoPriority]
                          ?.color,
                    }}
                  />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Status
                  </Typography>
                  <Chip
                    label={
                      statusColors[selectedTodo.status as TodoStatus]?.label ||
                      selectedTodo.status
                    }
                    size="small"
                    sx={{
                      bgcolor:
                        statusColors[selectedTodo.status as TodoStatus]?.bg,
                      color:
                        statusColors[selectedTodo.status as TodoStatus]?.color,
                    }}
                  />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Source Month
                  </Typography>
                  <Typography variant="body1">
                    {selectedTodo.source_month
                      ? new Date(selectedTodo.source_month).toLocaleDateString(
                          "en-US",
                          { year: "numeric", month: "long" },
                        )
                      : "-"}
                  </Typography>
                </Grid>
              </Grid>
              {selectedTodo.sample_queries &&
                selectedTodo.sample_queries.length > 0 && (
                  <Box>
                    <Typography
                      variant="subtitle2"
                      color="text.secondary"
                      sx={{ mb: 1 }}
                    >
                      Sample Queries ({selectedTodo.sample_queries.length})
                    </Typography>
                    <Paper
                      sx={{
                        p: 2,
                        bgcolor: "grey.50",
                        maxHeight: 200,
                        overflow: "auto",
                      }}
                    >
                      {selectedTodo.sample_queries.map(
                        (query: string, idx: number) => (
                          <Typography
                            key={idx}
                            variant="body2"
                            sx={{
                              mb: 1,
                              pb: 1,
                              borderBottom:
                                idx < selectedTodo.sample_queries!.length - 1
                                  ? "1px solid #e0e0e0"
                                  : "none",
                            }}
                          >
                            {idx + 1}. {query}
                          </Typography>
                        ),
                      )}
                    </Paper>
                  </Box>
                )}
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Created
                  </Typography>
                  <Typography variant="body1">
                    {new Date(selectedTodo.created_at).toLocaleString()}
                  </Typography>
                </Grid>
                {selectedTodo.completed_at && (
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Completed
                    </Typography>
                    <Typography variant="body1">
                      {new Date(selectedTodo.completed_at).toLocaleString()}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTodoViewDialog(false)}>Close</Button>
          {selectedTodo &&
            !selectedTodo.jira_ticket_key &&
            selectedTodo.status !== "dismissed" && (
              <Button
                variant="contained"
                startIcon={<JiraIcon />}
                onClick={() => {
                  handleCreateJira(selectedTodo);
                  setTodoViewDialog(false);
                }}
              >
                Create JIRA Ticket
              </Button>
            )}
        </DialogActions>
      </Dialog>

      {/* Analyze Dialog */}
      <Dialog
        open={analyzeDialog}
        onClose={() => setAnalyzeDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Analyze Monthly Patterns</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Scan CacheReview entries for the selected month to find recurring
            patterns and create AI todos.
            {unanalyzedData?.unanalyzed_count ? (
              <Alert severity="info" sx={{ mt: 2 }}>
                There are <strong>{unanalyzedData.unanalyzed_count}</strong>{" "}
                unanalyzed entries waiting to be processed.
              </Alert>
            ) : null}
          </Typography>
          <Box sx={{ display: "flex", gap: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Year</InputLabel>
              <Select
                value={analyzeYear}
                label="Year"
                onChange={(e) => setAnalyzeYear(e.target.value as number)}
              >
                {[2024, 2025, 2026].map((year) => (
                  <MenuItem key={year} value={year}>
                    {year}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Month</InputLabel>
              <Select
                value={analyzeMonth}
                label="Month"
                onChange={(e) => setAnalyzeMonth(e.target.value as number)}
              >
                {months.map((month, idx) => (
                  <MenuItem key={idx} value={idx + 1}>
                    {month}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAnalyzeDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => analyzeMutation.mutate()}
            disabled={analyzeMutation.isPending}
            startIcon={
              analyzeMutation.isPending ? (
                <CircularProgress size={18} />
              ) : (
                <AnalyzeIcon />
              )
            }
          >
            {analyzeMutation.isPending ? "Analyzing..." : "Analyze"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Interaction View Dialog */}
      <Dialog
        open={interactionViewDialog}
        onClose={() => setInteractionViewDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Interaction Details
          {selectedInteraction?.was_cache_hit && (
            <Chip
              label="Cache Hit"
              size="small"
              color="success"
              sx={{ ml: 2 }}
            />
          )}
        </DialogTitle>
        <DialogContent>
          {selectedInteraction && (
            <Box
              sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}
            >
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  User Query
                </Typography>
                <Paper sx={{ p: 2, bgcolor: "grey.50" }}>
                  <Typography variant="body1" sx={{ whiteSpace: "pre-wrap" }}>
                    {selectedInteraction.user_query}
                  </Typography>
                </Paper>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Bot Response
                </Typography>
                <Paper
                  sx={{
                    p: 2,
                    bgcolor: "grey.50",
                    maxHeight: 300,
                    overflow: "auto",
                  }}
                >
                  <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                    {selectedInteraction.bot_response}
                  </Typography>
                </Paper>
              </Box>
              {selectedInteraction.external_context && (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    External Context Fetched
                  </Typography>
                  <Paper
                    sx={{
                      p: 2,
                      bgcolor: "#F0F9FF",
                      maxHeight: 150,
                      overflow: "auto",
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        whiteSpace: "pre-wrap",
                        fontFamily: "monospace",
                        fontSize: "0.8rem",
                      }}
                    >
                      {selectedInteraction.external_context.substring(0, 1000)}
                      {selectedInteraction.external_context.length > 1000 &&
                        "..."}
                    </Typography>
                  </Paper>
                </Box>
              )}
              <Grid container spacing={2}>
                <Grid item xs={6} sm={3}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Context Type
                  </Typography>
                  <Chip
                    label={selectedInteraction.context_type}
                    size="small"
                    variant="outlined"
                  />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="subtitle2" color="text.secondary">
                    User
                  </Typography>
                  <Typography variant="body2">
                    {selectedInteraction.user_email || "-"}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Processing Time
                  </Typography>
                  <Typography variant="body2">
                    {selectedInteraction.processing_time_ms
                      ? `${selectedInteraction.processing_time_ms}ms`
                      : "-"}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Cache Hit
                  </Typography>
                  <Typography variant="body2">
                    {selectedInteraction.was_cache_hit ? "Yes" : "No"}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Date
                  </Typography>
                  <Typography variant="body2">
                    {new Date(selectedInteraction.created_at).toLocaleString()}
                  </Typography>
                </Grid>
                {selectedInteraction.thread_id && (
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Thread ID
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ fontFamily: "monospace", fontSize: "0.8rem" }}
                    >
                      {selectedInteraction.thread_id}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInteractionViewDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
